import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, SunMoon } from 'lucide-react';

interface ImageGalleryModalProps {
    images: Array<{ src: string; alt?: string; type?: string }>;
    initialIndex: number;
    onClose: () => void;
}

export const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({
    images,
    initialIndex,
    onClose
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [inverted, setInverted] = useState(() => localStorage.getItem('sb_image_invert') === 'true');

    const currentImage = images[currentIndex];
    const hasMultipleImages = images.length > 1;

    // Reset zoom when changing images
    useEffect(() => {
        setZoomLevel(1);
    }, [currentIndex]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
            if (e.key === 'ArrowLeft' && currentIndex > 0) {
                e.preventDefault();
                setCurrentIndex(prev => prev - 1);
            }
            if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
                e.preventDefault();
                setCurrentIndex(prev => prev + 1);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, images.length, onClose]);

    // Zoom controls
    const handleZoomIn = () => setZoomLevel(prev => Math.min(3, prev + 0.1));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(1, prev - 0.1));

    const modalContent = (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="relative w-full max-w-5xl max-h-[90vh] bg-gray-900 rounded-xl shadow-2xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
                    <div className="flex items-center gap-3">
                        <span className="text-white font-medium">
                            {currentImage.alt || 'Image'}
                        </span>
                        {hasMultipleImages && (
                            <span className="text-gray-400 text-sm">
                                {currentIndex + 1} / {images.length}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                const next = !inverted;
                                setInverted(next);
                                localStorage.setItem('sb_image_invert', String(next));
                            }}
                            title={inverted ? 'Tắt đảo màu' : 'Đảo màu (dark mode)'}
                            className={`p-2 rounded-lg transition-colors ${inverted ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
                        >
                            <SunMoon size={18} />
                        </button>
                        <button
                            onMouseDown={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                            className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Image */}
                <div
                    className={`flex-1 flex items-center justify-center p-4 min-h-[300px] relative transition-colors duration-200 ${inverted ? 'bg-gray-900' : 'bg-white'}`}
                    onWheel={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const delta = e.deltaY > 0 ? -0.03 : 0.03;
                        setZoomLevel(prev => Math.max(1, Math.min(3, prev + delta)));
                    }}
                >
                    <img
                        src={currentImage.src}
                        alt={currentImage.alt}
                        className="max-w-full max-h-[70vh] object-contain transition-all duration-200"
                        style={{
                            transform: `scale(${zoomLevel})`,
                            filter: inverted ? 'invert(1) hue-rotate(180deg)' : 'none',
                        }}
                        draggable={false}
                    />

                    {/* Nav Arrows */}
                    {hasMultipleImages && (
                        <>
                            <button
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
                                }}
                                disabled={currentIndex === 0}
                                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full disabled:opacity-30"
                            >
                                <ChevronLeft size={24} />
                            </button>
                            <button
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    if (currentIndex < images.length - 1) setCurrentIndex(prev => prev + 1);
                                }}
                                disabled={currentIndex === images.length - 1}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full disabled:opacity-30"
                            >
                                <ChevronRight size={24} />
                            </button>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-center px-4 py-3 bg-gray-800 border-t border-gray-700">
                    <div className="flex items-center gap-2">
                        <button
                            onMouseDown={(e) => { e.stopPropagation(); handleZoomOut(); }}
                            disabled={zoomLevel <= 1}
                            className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white disabled:opacity-50"
                        >
                            <ZoomOut size={18} />
                        </button>
                        <span className="text-gray-300 text-sm w-12 text-center">{Math.round(zoomLevel * 100)}%</span>
                        <button
                            onMouseDown={(e) => { e.stopPropagation(); handleZoomIn(); }}
                            disabled={zoomLevel >= 3}
                            className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white disabled:opacity-50"
                        >
                            <ZoomIn size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};
